import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../api/api';
import { gmDisplayName } from '../utils/displayName';

const Finals = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('de') ? 'de' : 'en';

  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/seasons').then(res => {
      setSeasons(res.data);
      const active = res.data.find(s => s.isActive);
      if (active) setSelectedSeason(active.id);
      else if (res.data.length > 0) setSelectedSeason(res.data[0].id);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedSeason) return;
    setLoading(true);
    setError('');
    api.get(`/playoffs/finals/${selectedSeason}`)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(err => {
        setError(err.response?.data?.error || t('finals.notStarted'));
        setData(null);
        setLoading(false);
      });
  }, [selectedSeason]);

  // ── helpers ──────────────────────────────────────────────────────────────

  const fmt = (val, decimals = 1) => (val !== null && val !== undefined) ? Number(val).toFixed(decimals) : '–';
  const pct = (val) => (val !== null && val !== undefined) ? `${Number(val).toFixed(1)}%` : '–';
  const round = (key) => {
    const labels = {
      de: { 'Play-In': 'Play-In', 'First Round': '1. Runde', 'Conference Semifinals': 'Conf. Halbfinale', 'Conference Finals': 'Conf. Finale', 'Finals': 'Finale' },
      en: { 'Play-In': 'Play-In', 'First Round': '1st Round', 'Conference Semifinals': 'Conf. Semifinals', 'Conference Finals': 'Conf. Finals', 'Finals': 'Finals' }
    };
    return (labels[lang] || labels.en)[key] || key;
  };

  const StatRow = ({ label, v1, v2, higherIsBetter = true }) => {
    const n1 = parseFloat(v1);
    const n2 = parseFloat(v2);
    const t1Better = !isNaN(n1) && !isNaN(n2) && (higherIsBetter ? n1 > n2 : n1 < n2);
    const t2Better = !isNaN(n1) && !isNaN(n2) && (higherIsBetter ? n2 > n1 : n2 < n1);
    return (
      <div className="flex items-center py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
        <span className={`w-1/3 text-right text-sm font-semibold pr-3 ${t1Better ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`}>{v1}</span>
        <span className="w-1/3 text-center text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</span>
        <span className={`w-1/3 text-left text-sm font-semibold pl-3 ${t2Better ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`}>{v2}</span>
      </div>
    );
  };

  const PlayerCard = ({ p, teamColor }) => {
    if (!p) return null;
    const name = p.player ? `${p.player.firstName} ${p.player.lastName}` : '–';
    const po = p.poAvg;
    const fin = p.finalsAvg;
    const hasFinals = fin && fin.games > 0;

    const diff = (poVal, finVal) => {
      if (!hasFinals || finVal === null) return null;
      const d = finVal - poVal;
      if (Math.abs(d) < 0.05) return null;
      return d;
    };

    const Diff = ({ val }) => {
      if (val === null || val === undefined) return null;
      const positive = val > 0;
      return (
        <span className={`text-xs ml-1 font-medium ${positive ? 'text-green-500' : 'text-red-400'}`}>
          {positive ? '+' : ''}{val.toFixed(1)}
        </span>
      );
    };

    const statLine = [
      { key: 'pts', label: lang === 'de' ? 'PKT' : 'PTS', po: po.pts, fin: fin?.pts },
      { key: 'reb', label: lang === 'de' ? 'REB' : 'REB', po: po.reb, fin: fin?.reb },
      { key: 'ast', label: lang === 'de' ? 'AST' : 'AST', po: po.ast, fin: fin?.ast },
      { key: 'stl', label: lang === 'de' ? 'STL' : 'STL', po: po.stl, fin: fin?.stl },
      { key: 'blk', label: lang === 'de' ? 'BLK' : 'BLK', po: po.blk, fin: fin?.blk },
    ];

    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-3 py-2 text-sm font-bold text-white truncate" style={{ backgroundColor: teamColor || '#3b82f6' }}>
          {name}
          <span className="ml-2 text-xs font-normal opacity-80">
            {po.games}G {lang === 'de' ? 'PO' : 'PO'}
            {hasFinals && ` · ${fin.games}G ${lang === 'de' ? 'Finale' : 'Finals'}`}
          </span>
        </div>
        <div className="px-3 py-2">
          {/* Header row */}
          <div className="flex text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
            <span className="w-10"></span>
            <span className="flex-1 text-center">{lang === 'de' ? 'PO Ø' : 'PO avg'}</span>
            {hasFinals && <span className="flex-1 text-center">{lang === 'de' ? 'Finale Ø' : 'Finals avg'}</span>}
          </div>
          {statLine.map(s => (
            <div key={s.key} className="flex items-center py-0.5">
              <span className="w-10 text-xs text-gray-500 dark:text-gray-400">{s.label}</span>
              <span className="flex-1 text-center text-sm font-semibold text-gray-800 dark:text-gray-200">{fmt(s.po)}</span>
              {hasFinals && (
                <span className="flex-1 text-center text-sm font-semibold text-gray-800 dark:text-gray-200">
                  {fmt(s.fin)}
                  <Diff val={diff(s.po, s.fin)} />
                </span>
              )}
            </div>
          ))}
          {po.fgPct !== null && (
            <div className="flex items-center py-0.5">
              <span className="w-10 text-xs text-gray-500 dark:text-gray-400">FG%</span>
              <span className="flex-1 text-center text-sm font-semibold text-gray-800 dark:text-gray-200">{pct(po.fgPct)}</span>
              {hasFinals && (
                <span className="flex-1 text-center text-sm font-semibold text-gray-800 dark:text-gray-200">
                  {pct(fin?.fgPct)}
                  <Diff val={fin?.fgPct !== null ? diff(po.fgPct, fin?.fgPct) : null} />
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-yellow-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 py-8">
        <div className="container mx-auto px-4 text-center py-20">
          <div className="text-4xl mb-4">🏆</div>
          <div className="text-xl text-gray-500 dark:text-gray-400">{t('common.loading', 'Loading…')}</div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-yellow-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 py-8">
        <div className="container mx-auto px-4 py-8">
          {seasons.length > 1 && (
            <div className="mb-6 flex justify-center">
              <select
                value={selectedSeason}
                onChange={e => setSelectedSeason(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200"
              >
                {seasons.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🏆</div>
            <div className="text-xl text-gray-500 dark:text-gray-400">{error}</div>
            <Link to="/playoffs" className="mt-6 inline-block text-blue-600 dark:text-blue-400 hover:underline">
              ← {t('nav.playoffs', 'Playoffs')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const { series, team1, team2, headToHead, narrative } = data;
  const t1 = team1.info;
  const t2 = team2.info;
  const t1Color = t1.primaryColor || '#1e40af';
  const t2Color = t2.primaryColor || '#dc2626';
  const t1Name = `${t1.city} ${t1.name}`;
  const t2Name = `${t2.city} ${t2.name}`;
  const t1Wins = series.higherSeedWins || 0;
  const t2Wins = series.lowerSeedWins  || 0;
  const isComplete = series.isComplete;
  const champion = isComplete ? (series.winnerId === t1.id ? t1 : t2) : null;
  const h2hRS = headToHead.regularSeason;

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-yellow-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">

      {/* Season selector */}
      {seasons.length > 1 && (
        <div className="flex justify-center pt-4 px-4">
          <select
            value={selectedSeason}
            onChange={e => setSelectedSeason(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-sm"
          >
            {seasons.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* ── HERO ── */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/20 to-yellow-600/10 dark:from-yellow-600/10 dark:to-transparent" />
        <div className="relative container mx-auto px-4 py-10 text-center">
          {isComplete
            ? <div className="text-5xl mb-2">🏆</div>
            : <div className="text-4xl mb-2">⚔️</div>
          }
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight mb-1">
            {lang === 'de' ? 'Das Finale' : 'The Finals'}
          </h1>
          {isComplete && champion && (
            <p className="text-yellow-600 dark:text-yellow-400 font-bold text-lg mb-4">
              🥇 {champion.city} {champion.name} – {lang === 'de' ? 'Champion!' : 'Champions!'}
            </p>
          )}

          {/* Score board */}
          <div className="inline-flex items-center gap-4 bg-white dark:bg-gray-800 rounded-2xl shadow-lg px-6 py-4 border border-gray-100 dark:border-gray-700 mt-2">
            {/* Team 1 */}
            <div className="text-center min-w-[100px]">
              {t1.logoUrl && (
                <img src={t1.logoUrl} alt={t1.abbreviation} className="w-12 h-12 object-contain mx-auto mb-1" />
              )}
              <div className="font-bold text-gray-900 dark:text-white text-sm leading-tight">{t1Name}</div>
              {team1.gm && (
                <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  GM: {gmDisplayName(team1.gm)}
                </div>
              )}
            </div>

            {/* Score */}
            <div className="flex items-center gap-3">
              <span className={`text-5xl font-black ${t1Wins > t2Wins ? 'text-green-500' : 'text-gray-700 dark:text-gray-300'}`}>{t1Wins}</span>
              <span className="text-2xl text-gray-400 dark:text-gray-500 font-light">–</span>
              <span className={`text-5xl font-black ${t2Wins > t1Wins ? 'text-green-500' : 'text-gray-700 dark:text-gray-300'}`}>{t2Wins}</span>
            </div>

            {/* Team 2 */}
            <div className="text-center min-w-[100px]">
              {t2.logoUrl && (
                <img src={t2.logoUrl} alt={t2.abbreviation} className="w-12 h-12 object-contain mx-auto mb-1" />
              )}
              <div className="font-bold text-gray-900 dark:text-white text-sm leading-tight">{t2Name}</div>
              {team2.gm && (
                <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  GM: {gmDisplayName(team2.gm)}
                </div>
              )}
            </div>
          </div>

          <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
            {series.seriesFormat} · {
              isComplete
                ? (lang === 'de' ? 'Abgeschlossen' : 'Complete')
                : (lang === 'de' ? 'Läuft noch' : 'In progress')
            }
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 pb-12 space-y-8 max-w-5xl">

        {/* ── HEAD-TO-HEAD THIS SEASON ── */}
        <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="bg-gradient-to-r from-gray-800 to-gray-700 dark:from-gray-700 dark:to-gray-600 px-5 py-3">
            <h2 className="text-white font-bold text-sm uppercase tracking-wider">
              {lang === 'de' ? '🔁 Bilanz diese Saison' : '🔁 Season Head-to-Head'}
            </h2>
          </div>
          <div className="px-5 py-4">
            {/* Regular season H2H */}
            {h2hRS.games.length > 0 ? (
              <>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                  {lang === 'de' ? 'Reguläre Saison' : 'Regular Season'}
                </p>
                <div className="flex items-center justify-center gap-6 mb-4">
                  <div className="text-center">
                    <div className="text-3xl font-black text-gray-900 dark:text-white">{h2hRS.t1Wins}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t1.abbreviation}</div>
                  </div>
                  <div className="text-gray-300 dark:text-gray-600 text-xl">–</div>
                  <div className="text-center">
                    <div className="text-3xl font-black text-gray-900 dark:text-white">{h2hRS.t2Wins}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t2.abbreviation}</div>
                  </div>
                </div>
                <div className="space-y-1">
                  {h2hRS.games.map((g, i) => (
                    <div key={i} className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded px-3 py-1.5">
                      <span className={`font-semibold ${g.t1Won ? 'text-green-600 dark:text-green-400' : ''}`}>{t1.abbreviation}</span>
                      <span className="font-bold text-gray-800 dark:text-gray-200">{g.t1Score} – {g.t2Score}</span>
                      <span className={`font-semibold ${!g.t1Won ? 'text-green-600 dark:text-green-400' : ''}`}>{t2.abbreviation}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-400 italic text-center py-2">
                {lang === 'de' ? 'Keine Begegnung in der regulären Saison.' : 'Did not meet in the regular season.'}
              </p>
            )}

            {/* Previous playoff meetings */}
            {headToHead.playoffs.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  {lang === 'de' ? 'Früheres Playoff-Aufeinandertreffen' : 'Earlier Playoff Meeting'}
                </p>
                {headToHead.playoffs.map((po, i) => (
                  <div key={i} className="flex justify-between items-center text-xs bg-blue-50 dark:bg-blue-900/20 rounded px-3 py-1.5">
                    <span className="text-blue-700 dark:text-blue-300 font-medium">{round(po.round)}</span>
                    <span className="font-bold text-gray-800 dark:text-gray-200">{po.t1Wins} – {po.t2Wins}</span>
                    <span className={`font-semibold ${po.won ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                      {po.won ? (lang === 'de' ? `${t1.abbreviation} ✓` : `${t1.abbreviation} ✓`) : `${t2.abbreviation} ✓`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ── ROAD TO THE FINALS ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { team: t1, gm: team1.gm, road: team1.roadToFinals, narr: narrative.team1, color: t1Color, teamName: t1Name },
            { team: t2, gm: team2.gm, road: team2.roadToFinals, narr: narrative.team2, color: t2Color, teamName: t2Name }
          ].map(({ team, gm, road, narr, color, teamName }) => (
            <section key={team.id} className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div className="px-5 py-3 text-white font-bold text-sm flex items-center gap-2" style={{ backgroundColor: color }}>
                {team.logoUrl && <img src={team.logoUrl} alt="" className="w-5 h-5 object-contain" />}
                <span>{teamName}</span>
                {gm && <span className="ml-auto text-xs font-normal opacity-80">GM: {gmDisplayName(gm)}</span>}
              </div>
              <div className="px-5 py-4">
                {/* Narrative */}
                {narr && (
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4 italic border-l-4 pl-3" style={{ borderColor: color }}>
                    {narr[lang]}
                  </p>
                )}
                {/* Round-by-round */}
                <div className="space-y-1.5">
                  {road.map((r, i) => (
                    <Link key={i} to={`/playoffs/series/${r.seriesId}`} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-700/50 rounded px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                      <span className="text-gray-500 dark:text-gray-400 w-28 truncate">{round(r.round)}</span>
                      <span className="text-gray-600 dark:text-gray-400 truncate flex-1 text-center">
                        vs {r.opponent.city} {r.opponent.name}
                      </span>
                      <span className={`font-bold w-12 text-right ${r.won ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                        {r.won ? 'W' : 'L'} {r.teamWins}–{r.oppWins}
                      </span>
                    </Link>
                  ))}
                  {road.length === 0 && (
                    <p className="text-xs text-gray-400 italic">{lang === 'de' ? 'Keine früheren Playoff-Runden.' : 'No prior playoff rounds.'}</p>
                  )}
                </div>
              </div>
            </section>
          ))}
        </div>

        {/* ── TEAM STATS COMPARISON ── */}
        <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="bg-gradient-to-r from-gray-800 to-gray-700 dark:from-gray-700 dark:to-gray-600 px-5 py-3">
            <h2 className="text-white font-bold text-sm uppercase tracking-wider">
              {lang === 'de' ? '📊 Team-Statistiken Playoffs' : '📊 Team Stats – Playoffs'}
            </h2>
          </div>
          <div className="px-5 py-4">
            <div className="flex text-xs font-bold text-gray-500 dark:text-gray-400 mb-2">
              <span className="w-1/3 text-right pr-3 truncate">{t1.abbreviation}</span>
              <span className="w-1/3 text-center uppercase tracking-wide">{lang === 'de' ? 'Schnitt' : 'Avg'}</span>
              <span className="w-1/3 text-left pl-3 truncate">{t2.abbreviation}</span>
            </div>
            <StatRow label={lang === 'de' ? 'Punkte' : 'Points'} v1={fmt(team1.playoffAvg.ppg)} v2={fmt(team2.playoffAvg.ppg)} />
            <StatRow label={lang === 'de' ? 'Rebounds' : 'Rebounds'} v1={fmt(team1.playoffAvg.rpg)} v2={fmt(team2.playoffAvg.rpg)} />
            <StatRow label={lang === 'de' ? 'Assists' : 'Assists'} v1={fmt(team1.playoffAvg.apg)} v2={fmt(team2.playoffAvg.apg)} />
            <StatRow label="FG%" v1={pct(team1.playoffAvg.fgPct)} v2={pct(team2.playoffAvg.fgPct)} />
            <div className="flex text-xs text-gray-400 dark:text-gray-500 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
              <span className="w-1/3 text-right pr-3">{team1.playoffAvg.games}G</span>
              <span className="w-1/3 text-center">{lang === 'de' ? 'Spiele' : 'Games'}</span>
              <span className="w-1/3 text-left pl-3">{team2.playoffAvg.games}G</span>
            </div>
          </div>
        </section>

        {/* ── PLAYER COMPARISON TOP 3 ── */}
        <section>
          <h2 className="text-gray-700 dark:text-gray-300 font-bold text-sm uppercase tracking-wider mb-3 px-1">
            {lang === 'de' ? '🏀 Top-Spieler im Vergleich' : '🏀 Top Players Comparison'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Team 1 */}
            <div className="space-y-3">
              <div className="text-xs font-bold text-center uppercase tracking-wider" style={{ color: t1Color }}>{t1Name}</div>
              {team1.top3.length > 0
                ? team1.top3.map((p, i) => <PlayerCard key={i} p={p} teamColor={t1Color} />)
                : <p className="text-xs text-gray-400 italic text-center py-4">{lang === 'de' ? 'Noch keine Daten' : 'No data yet'}</p>
              }
            </div>
            {/* Team 2 */}
            <div className="space-y-3">
              <div className="text-xs font-bold text-center uppercase tracking-wider" style={{ color: t2Color }}>{t2Name}</div>
              {team2.top3.length > 0
                ? team2.top3.map((p, i) => <PlayerCard key={i} p={p} teamColor={t2Color} />)
                : <p className="text-xs text-gray-400 italic text-center py-4">{lang === 'de' ? 'Noch keine Daten' : 'No data yet'}</p>
              }
            </div>
          </div>
        </section>

        {/* ── FINALS GAMES ── */}
        {series.games && series.games.length > 0 && (
          <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="px-5 py-3" style={{ background: 'linear-gradient(to right, #92400e, #b45309)' }}>
              <h2 className="text-white font-bold text-sm uppercase tracking-wider">
                🏆 {lang === 'de' ? 'Spielergebnisse' : 'Game Results'}
              </h2>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {series.games.map(game => {
                const homeIsT1 = game.homeTeamId === t1.id;
                const t1Score = homeIsT1 ? game.homeScore : game.awayScore;
                const t2Score = homeIsT1 ? game.awayScore : game.homeScore;
                const t1Won = t1Score > t2Score;
                return (
                  <Link
                    key={game.id}
                    to={`/game/${game.id}`}
                    className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-yellow-600 dark:text-yellow-400 w-14">
                        {lang === 'de' ? `Spiel ${game.gameNumber}` : `Game ${game.gameNumber}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 flex-1 justify-center">
                      <span className={`text-sm font-semibold ${t1Won ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>{t1.abbreviation}</span>
                      <span className="text-base font-black text-gray-900 dark:text-white">{t1Score} – {t2Score}</span>
                      <span className={`text-sm font-semibold ${!t1Won ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>{t2.abbreviation}</span>
                    </div>
                    <span className="text-xs text-blue-500 dark:text-blue-400 hover:underline w-14 text-right">
                      {lang === 'de' ? 'Details →' : 'Details →'}
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Back link */}
        <div className="text-center pb-4">
          <Link to="/playoffs" className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
            ← {t('nav.playoffs', 'Playoffs')}
          </Link>
        </div>

      </div>
    </div>
  );
};

export default Finals;
